import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, Input, LOCALE_ID, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, CommandBarComponent, IconCommand } from "@tylertech/commands-module";
import { BusyIndicatorComponent, formatAbbreviatedNumber, TruncateTextComponent } from "@tylertech/common-components-module";
import { FileService, LoggingService, NavigationService, UrlService, } from "@tylertech/core-module";
import { ForgeCardModule, ForgeIconButtonModule, ForgeIconModule, ForgeToolbarModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { IntentService } from "@tylertech/intents-module";
import { AppLinkParms, ExecutionResult, FilterItem, IChartCriteria, ProfileSettingViewModel, } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "general-ledger-monthly-spending-chart",
    templateUrl: "./monthlySpendingChart.tmpl.html",
    styleUrls: ["./monthlySpendingChart.component.scss"],
    imports: [
        BusyIndicatorComponent,
        ChartJsContainerComponent,
        CommandBarComponent,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
        TruncateTextComponent,
    ]
})
export class MonthlySpendingChartComponent implements OnDestroy, OnInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $loggingService = inject(LoggingService);
    private readonly $fileService = inject(FileService);
    private readonly $urlService = inject(UrlService);
    private readonly $intentService = inject(IntentService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartService = inject(ChartService);

    @Input("chartId") private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;

    private readonly destructor$ = new Subject<void>();
    private _chartCriteria: IChartCriteria;

    readonly commands: ReadonlyArray<Command>;
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"bar">;

    isLoaded: boolean;
    ChartTitle: string = "Monthly Spending";
    bcfId: string;
    fiscalId: string;
    groupById: string;
    month: number;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.chartHasData,
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.chartCriteria?.ChartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartSubtitle(): string
    {
        let ret = "";
        if (!this.chartCriteria?.Parameters)
        {
            return ret;
        }

        const { bcfId, month, groupById } = this.chartCriteria.Parameters.reduce(
            (v, x) =>
            {
                switch (x.Id)
                {
                    case "SpendingFiscal":
                        v.bcfId = x;
                        break;
                    case "SpendingMonth":
                        v.month = x;
                        break;
                    case "SpendingGroupById":
                        v.groupById = x;
                        break;
                }
                return v;
            },
            {} as Record<"bcfId" | "month" | "groupById", ProfileSettingViewModel | undefined>);

        if (bcfId?.Value)
        {
            const spFiscal = bcfId.Options.find(x => x.Value === bcfId.Value);
            if (spFiscal)
            {
                ret = ret + " by " + spFiscal.Display;
            }
        }
        if (month?.Value && month?.Options)
        {
            const mon = month.Options.find(x => x.Value === month.Value);
            if (mon)
            {
                ret = ret + (ret.length > 0 ? " &bull; " : " ") + mon.Display;
            }
        }
        if (groupById?.Value)
        {
            const groupByDesc = groupById.Options.find(x => x.Value === groupById.Value);
            if (groupByDesc)
            {
                ret = ret + (ret.length > 0 ? " &bull; by " : " ") + groupByDesc.Display;
            }
        }
        return ret;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            }
            catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    chartClass(): string
    {
        return this.chartId.replace(".", "-").toLowerCase() + "-class";
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService
            .GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.hasData())
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService.showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        const chartDataColors = this.$chartService.getColors(this.chartCriteria.ChartDatas[0].Data.length);

        this.month = Number(this.month);
        this.chartConfig = {
            type: "bar",
            options: {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    legend: {
                        position: "right",
                        display: false,
                    },
                    tooltip: {
                        callbacks: {
                            label: (tooltipItem: TooltipItem<"bar">): string | Array<string> =>
                            {
                                let label = ({
                                    type: "category",
                                    display: true,
                                    gridLines: { display: true },
                                    labels: this.chartCriteria.ChartDatas[0].Labels,
                                }).labels[tooltipItem.dataIndex];
                                const filterParam = this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].FilterParam;
                                if (filterParam)
                                {
                                    label = `${filterParam.Value}`;
                                }
                                const value = Number(tooltipItem.raw);
                                return `${label}: ${formatCurrency(value, this.locale, "$")}`;
                            },
                        },
                    },
                },
                // TODO(DO-7754) - Can some of this be standardize and pulled out somewhere?
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], chart: Chart): void =>
                {
                    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
                    const element = elements[0];
                    if (!element) return null;
                    const gr = this.chartCriteria?.Parameters?.find(v => v.Id === "SpendingGroupById");
                    const bcfId = this.chartCriteria?.Parameters?.find(x => x.Id === "SpendingFiscal")?.Value;
                    const month = this.chartCriteria?.Parameters?.find(x => x.Id === "SpendingMonth")?.Value;
                    const monthIndex = this.chartCriteria?.Parameters?.find(x => x.Id === "SpendingMonth")
                        ?.Options.map(x => x.Value)
                        .indexOf(month) + 1;
                    const groupBy = gr?.Options.find(x => x.Value === gr.Value)?.Display;
                    const index = element.index;
                    const filter: Array<FilterItem> = [
                        {
                            Key: "BudgetCodeFiscalId",
                            Value: bcfId,
                        },
                        {
                            Key: "PeriodId",
                            Value: String(monthIndex),
                        },
                        {
                            Key: "NonZeroActivity",
                            Value: "true",
                        },
                        {
                            Key: "AccountType",
                            Value: "Expense",
                        },
                        {
                            Key: "QueryString",
                            Value: groupBy.toLowerCase() === "account name"
                                ? this.chartCriteria.ChartDatas[0].Labels[index]
                                : `"${groupBy}"="${this.chartCriteria.ChartDatas[0].Labels[index]}"`,
                        },
                    ];
                    const parms: AppLinkParms = {
                        App: "glaccounts",
                        Parms: filter,
                    };
                    this.$urlService.GetAppLink(parms).then((executionResult: ExecutionResult<string>) =>
                    {
                        const index = executionResult.Result.indexOf("?");
                        if (executionResult.Result.includes("apps/form"))
                        {
                            this.$navigationService.navigateToApp(executionResult.Result);
                        }
                        else if (index > 0)
                        {
                            this.$intentService.resolve("GLAccounts", "Search", {
                                querystring: executionResult.Result.substring(index, executionResult.Result.length),
                            });
                        }
                    });
                },
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    y: {
                        type: "linear",
                        display: true,
                        position: "right",
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                },
            },
            data: {
                datasets: [
                    {
                        type: "bar",
                        data: this.chartCriteria.ChartDatas[0].Data,
                        backgroundColor: chartDataColors,
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    },
                ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
